import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Box,
  Tabs,
  Tab,
  IconButton,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyIcon from '@mui/icons-material/Key';
import { getProjectLinksService, getProjectDownloadUrlsService } from '../../services/codeGenerateService';
import ClarityAnalytics from '../clarity';
import { useAuth } from "@clerk/clerk-react";



const BackendApiTab = ({
  gridContainerStyles,
  rightColumnStylesDeployment,
  selectedProject,
  iframeKey,
  fetchCredits,
}) => {
  const [apiUrls, setApiUrls] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [credentials, setCredentials] = useState({});
  const [downloadDialog, setDownloadDialog] = useState({ open: false, message: '' });
  const [loading, setLoading] = useState(false); // Added loading state
  const { getToken } = useAuth();

  const initializeBackendTab = async () => {
    if (selectedProject) {
      setLoading(true);
      try {
        const freshToken = await getToken();
        localStorage.setItem("clerk-token", freshToken);
        const linksResponse = await getProjectLinksService(selectedProject);
        const { backend_swagger_url, project_dnd_url, project_frontend_url } = linksResponse.data.links;
        const currentDomain = window.location.origin;
        const deploymentPendingUrl = `${currentDomain}/deployment-pending`;

        setCredentials({
          username: linksResponse.data.username,
          password: linksResponse.data.password,
        });
        setApiUrls({
          SERVER: backend_swagger_url === "#" ? deploymentPendingUrl : backend_swagger_url,
          DESIGNER: project_dnd_url === "#" ? deploymentPendingUrl : project_dnd_url,
          PREVIEW: project_frontend_url === "#" ? deploymentPendingUrl : project_frontend_url + 'dnd/dnd/dndBuilder/preview?id=2',
        });
      } catch (error) {
        setApiUrls({
          SERVER: '',
          DESIGNER: '',
          PREVIEW: '',
        });
        console.error('Error initializing backend tab:', error);
      } finally {
        setLoading(false); // Stop loading
      }
    }
  };

  const downloadFile = async (url, prefix) => {
    try {
      console.log(`Attempting to download ${prefix} from URL:`, url);
      // Simply open the URL in a new tab
      window.open(url, '_blank');
    } catch (err) {
      console.error(`Error opening ${prefix} download link:`, err);
      throw new Error(`Failed to open ${prefix} download link: ${err.message}`);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      const freshToken = await getToken();
      localStorage.setItem("clerk-token", freshToken);
      const downloadResponse = await getProjectDownloadUrlsService(selectedProject);
      console.log('Download Response:', downloadResponse.data);
      
      if (downloadResponse.data.error) {
        setDownloadDialog({
          open: true,
          message: downloadResponse.data.error,
        });
        return;
      }

      const { backendDownloadUrl, frontendDownloadUrl } = downloadResponse.data;
      
      // Validate URLs before attempting download
      if (!backendDownloadUrl && !frontendDownloadUrl) {
        throw new Error('No download URLs provided');
      }

      const downloadPromises = [];
      if (backendDownloadUrl) {
        downloadPromises.push(downloadFile(backendDownloadUrl, 'backend'));
      }
      if (frontendDownloadUrl) {
        downloadPromises.push(downloadFile(frontendDownloadUrl, 'frontend'));
      }

      try {
        await Promise.all(downloadPromises);
        await fetchCredits();
      } catch (downloadError) {
        throw new Error(`Download failed: ${downloadError.message}`);
      }
    } catch (error) {
      console.error('Error during download:', error);
      setDownloadDialog({
        open: true,
        message: error.message || 'Failed to download files. Please check your network connection and try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initializeBackendTab();
  }, [selectedProject]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenInNewTab = (url) => {
    window.open(url, '_blank');
  };

  const renderIframe = (url, type) => (
    <Box position="relative" width="100%" height="100%">
      {url && url.includes('deployment-pending') ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          sx={{ backgroundColor: '#f5f5f5', padding: 3, textAlign: 'center' }}
        >
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Deployment in Progress
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your application is being deployed. Please refresh the page after a few minutes.
          </Typography>
        </Box>
      ) : (
        <iframe
          key={`${type}-${iframeKey}`}
          src={url}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: '4px',
          }}
          title={`API ${type}`}
          onLoad={(e) => {
            const token = localStorage.getItem('clerk-token');
            if (token && e.target.contentWindow) {
              e.target.contentWindow.postMessage({ type: 'SET_TOKEN', token }, '*');
            }
          }}
        />
      )}
      {url && (
        <IconButton
          onClick={() => handleOpenInNewTab(url)}
          sx={{
            position: 'absolute',
            top: 40,
            right: 4,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            zIndex: 1,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            },
          }}
          size="small"
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  return (
    <>
      <><ClarityAnalytics /></>
    <Box position="relative" width="100%" height="100%">
      <Grid container {...gridContainerStyles}>
        <Grid item {...rightColumnStylesDeployment}>
          <Paper
            elevation={1}
            sx={{
              height: '100%',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label="Server" />
                <Tab label="Designer" />
                <Tab label="Preview" />
              </Tabs>
            </Box>

            <Box sx={{ flexGrow: 1, position: 'relative' }}>
              {loading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                >
                  <CircularProgress />
                </Box>
              ) : (
                Object.entries({
                  SERVER: 0,
                  DESIGNER: 1,
                  PREVIEW: 2,
                }).map(([key, index]) => (
                  <Box
                    key={key}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: activeTab === index ? 'block' : 'none',
                    }}
                  >
                    {renderIframe(apiUrls[key], key.toLowerCase())}
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Credentials Box */}
      {!loading && (
        <Card
          sx={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            width: 250,
            backgroundColor: 'grey.900',
            color: 'white',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <KeyIcon fontSize="small" />
              <Typography variant="subtitle2">Credentials</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="grey.400">
                Username:
              </Typography>
              <Typography variant="body2">{credentials.username}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="grey.400">
                Password:
              </Typography>
              <Typography variant="body2">{credentials.password}</Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Download Button */}
      <Button
        variant="contained"
        sx={{
          color: 'white',
          backgroundColor: '#1a1a1a',
          textTransform: 'none',
          borderRadius: '8px',
          padding: '8px 16px',
          boxShadow: '0 4px 6px rgba(26, 26, 26, 0.4)',
          '&:hover': {
            backgroundColor: '#333',
          },
          position: 'absolute',
          bottom: 5,
          left: 16,
        }}
        onClick={handleDownload}
        disabled={loading} // Disable button while loading
      >
        {loading ? 'Processing...' : 'Download Project'}
      </Button>

      {/* Insufficient Balance Dialog */}
      <Dialog
        open={downloadDialog.open}
        onClose={() => setDownloadDialog({ open: false, message: '' })}
      >
        <DialogTitle>Download Error</DialogTitle>
        <DialogContent>
          <Typography>{downloadDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDownloadDialog({ open: false, message: '' })} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </>
  );
};

export default BackendApiTab;
